import { CheckCircle2, XCircle, Loader2, Clock, AlertCircle } from 'lucide-react'
import type { DiscoveryJob } from '@/api/client'
import { cn } from '@/lib/utils'

interface Props {
  job: DiscoveryJob
  compact?: boolean
}

const statusConfig: Record<string, { label: string; cls: string; icon: any }> = {
  Scheduled: { label: 'Agendado', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock },
  Queued: { label: 'Na fila', cls: 'bg-slate-50 text-slate-700 border-slate-200', icon: Clock },
  Running: { label: 'Em execução', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Loader2 },
  Completed: { label: 'Concluído', cls: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle2 },
  Failed: { label: 'Falhou', cls: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  Cancelled: { label: 'Cancelado', cls: 'bg-slate-50 text-slate-500 border-slate-200', icon: XCircle },
}

export function DiscoveryProgressBar({ job, compact = false }: Props) {
  const cfg = statusConfig[job.status] || statusConfig.Queued
  const Icon = cfg.icon
  const pct = job.progressTotal > 0
    ? Math.min(100, Math.round((job.progressCurrent / job.progressTotal) * 100))
    : 0
  const isActive = job.status === 'Running' || job.status === 'Scheduled'

  // Countdown se estiver Scheduled
  let countdown = ''
  if (job.status === 'Scheduled' && job.scheduledStartAt) {
    const ms = new Date(job.scheduledStartAt).getTime() - Date.now()
    if (ms > 0) {
      const seconds = Math.ceil(ms / 1000)
      countdown = `Inicia em ${seconds}s`
    }
  }

  return (
    <div className={cn('rounded-lg border p-3', cfg.cls)}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Icon className={cn('h-4 w-4', job.status === 'Running' && 'animate-spin')} />
          <span>{cfg.label}</span>
          {countdown && <span className="text-xs opacity-75">— {countdown}</span>}
        </div>
        {isActive && job.progressTotal > 0 && (
          <span className="text-xs font-mono">{pct}%</span>
        )}
      </div>

      {!compact && (
        <>
          <div className="text-xs mb-2 opacity-90">{job.currentStep || '...'}</div>

          {(isActive || job.status === 'Completed') && (
            <div className="h-2 bg-white/60 rounded-full overflow-hidden mb-2">
              <div
                className={cn(
                  'h-full transition-all duration-500',
                  job.status === 'Completed' ? 'bg-green-500' : 'bg-brand-500'
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
          )}

          {job.status === 'Failed' && job.errorMessage && (
            <div className="flex items-start gap-1 text-xs">
              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
              <span className="break-words">{job.errorMessage}</span>
            </div>
          )}

          {(job.status === 'Running' || job.status === 'Completed') && (
            <div className="flex gap-3 text-xs mt-1 opacity-80">
              {job.usersCount != null && <span><strong>{job.usersCount}</strong> usuários</span>}
              {job.groupsCount != null && <span><strong>{job.groupsCount}</strong> grupos</span>}
              {job.domainsCount != null && <span><strong>{job.domainsCount}</strong> domínios</span>}
              {job.conflictsCount != null && job.conflictsCount > 0 && (
                <span className="text-red-700"><strong>{job.conflictsCount}</strong> conflitos</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
